import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  createTheme,
  Divider,
  Paper,
  styled,
  Tab,
  Tabs,
  ThemeProvider,
  Toolbar,
} from '@mui/material';
import { ForkKnife } from '@phosphor-icons/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { BASE_URL, BASE_URL_SOCKET } from 'src/baseUrl';
import { RootState } from 'src/redux/reducers';
import Service from 'src/setup/network';
import './kds.css';
import { toastAlert } from 'src/utils/toastAlert';
import { socketConnected } from 'src/redux/action/socket.action';
import { useDispatch } from 'react-redux';
import { kotPrint } from 'src/components/bill-print/kot';

const theme = createTheme({
  components: {
    // Name of the component
    MuiAppBar: {
      styleOverrides: {
        root: {
          left: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#8943EF',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: 'Poppins',
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '16px',
          lineHeight: '24px',
          color: '#818091',
          textTransform: 'none',
          '&.Mui-selected': {
            fontFamily: 'Poppins',
            fontStyle: 'normal',
            fontWeight: '600',
            fontSize: '16px',
            lineHeight: '24px',
            color: '#2E2E2E',
            textTransform: 'none',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '100vw !important',
        },
      },
    },
  },
});

export const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  // padding: theme.spacing(1),
  fontFamily: 'Poppins',
  fontSize: '16px',
  lineHeight: '24px',
  color: '#2E2E2E',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'none',
}));

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface OrdersInterface {
  All: any[];
  Queue: any[];
  Cooking: any[];
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
  data: any[]; // Adjust this type according to the actual data structure
  other?: React.HTMLAttributes<HTMLDivElement>; // This is for any additional props
}

interface Order {
  id: string;
  status: string;
  ordersItems: any[];
}

interface OrderItem {
  itemId: string;
  identifierId: string;
  comboId: string;
  customId: string;
}

const KDSHome = (props: any) => {
  const dispatch = useDispatch();
  const userInfo = JSON.parse(window.localStorage.getItem('userInfo') || '');
  const auth = useSelector(({ authReducer }: RootState) => authReducer);
  const { notFirst, socket } = useSelector(
    ({ socketData }: RootState) => socketData
  );
  const { settingData } = useSelector(
    ({ settingData }: RootState) => settingData,
    shallowEqual
  );
  const businessName = settingData?.businessInfo?.name;
  const businessAddress = settingData?.businessInfo?.address;

  const [audio] = useState(new Audio());
  const [values, setValues] = useState<{
    index: number;
    first: boolean;
    orders: OrdersInterface;
    errorPosition: string;
    errorText: string;
    isKitchenBusy: boolean;
    loadInd: any | null;
    toPrint: any | null;
  }>({
    index: 0,
    first: true,
    orders: { All: [], Queue: [], Cooking: [] }, // Initialize All as an empty array
    errorPosition: '',
    errorText: '',
    isKitchenBusy: false,
    loadInd: null,
    toPrint: null,
  });
  const {
    index,
    first,
    orders,
    errorPosition,
    errorText,
    isKitchenBusy,
    loadInd,
    toPrint,
  } = values;
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setValues((x) => ({ ...x, first: true }));
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // get all orders API call
  useEffect(() => {
    if (first) fetchOrders();
  }, [first]);

  const fetchOrders = async () => {
    const { data: res } = await Service.get('/orders/kds');
    if (res.success) {
      setValues((x) => ({
        ...x,
        orders: {
          ...orders,
          All: res.data?.orders ?? [],
          Cooking:
            res.data?.orders.filter((x: any) => x.status === 'cooking') ?? [],
          Queue:
            res.data?.orders?.filter((x: any) => x.status === 'preparing') ??
            [],
        },
        first: false,
        isKitchenBusy: res.isKitchenBusy,
      }));
    }
  };
  // intervals for time
  useEffect(() => {
    setInterval(() => setDate(new Date()), 30000);
  }, []);

  React.useEffect(() => {
    const roleInfo = window.localStorage.getItem('userInfo');
    const userInfo = roleInfo ? JSON.parse(roleInfo) : null;

    if (!socket && !notFirst) {
      const soc = io(BASE_URL_SOCKET, {
        reconnection: true,
        reconnectionAttempts: Infinity,
      });

      dispatch(socketConnected(soc));
    }

    if (socket) {
      socket.on('kdsOrderUpdateEmit', (res: any) => {
        audio.play();
        setValues((x) => ({
          ...x,
          orders: {
            ...orders,
            All: res ?? [],
            Cooking: res.filter((x: any) => x.status === 'cooking') ?? [],
            Queue: res.filter((x: any) => x.status === 'preparing') ?? [],
          },
        }));
      });
    }
  }, [socket, notFirst]);

  const handleChangeIndex = (e: any, newValue: any) => {
    setValues((prev) => ({ ...prev, index: newValue, first: true }));
  };

  const TabPanel: React.FC<TabPanelProps> = (props) => {
    const { children, value, index, data, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        className="w-100"
        {...other}
      >
        {value === index && (
          <>
            {data && (
              <App
                data={data}
                toPrint={toPrint}
                loadInd={loadInd}
                setValues={setValues}
                businessName={businessName}
                businessAddress={businessAddress}
              />
            )}
          </>
        )}
      </div>
    );
  };

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  };

  const tabCustomLabel = (text: string, data: any, step: number) => (
    <span className="d-flex">
      {text}
      {data && data.length > 0 && (
        <span
          className={`ml-1 bg-almost-white rounded-[5px] py-[2px] px-1
          ${step === index ? 'text-primary' : 'text-secondary-0'}`}
        >
          {data && data.length > 0 && data.length}
        </span>
      )}
    </span>
  );

  return (
    <ThemeProvider theme={theme}>
      <div className="w-[calc(100vw-100px)]">
        <div className="w-full">
          <div className="flex flex-col">
            <AppBar className="">
              <Toolbar className="bg-pure-white">
                <div className="flex justify-between w-full items-center">
                  <span className="text-text-black">
                    {formatDateTimeKDS(date)}
                  </span>
                  <Box className="">
                    <Tabs value={index} centered onChange={handleChangeIndex}>
                      <Tab
                        label={tabCustomLabel('All', orders.All, 0)}
                        {...a11yProps(0)}
                      />
                      <Tab
                        label={tabCustomLabel('Queue', orders.Queue, 1)}
                        {...a11yProps(1)}
                      />
                      <Tab
                        label={tabCustomLabel('Cooking', orders.Cooking, 2)}
                        {...a11yProps(2)}
                      />
                    </Tabs>
                  </Box>

                  <Button></Button>
                </div>
              </Toolbar>
            </AppBar>

            <Toolbar />

            <Container style={{ width: '1200px' }}>
              <Box sx={{ my: 2 }}>
                <div className="kds_width_main">
                  <div className="kds_width">
                    <TabPanel value={index} index={0} data={orders.All}>
                      All
                    </TabPanel>
                    <TabPanel value={index} index={1} data={orders.Queue}>
                      Queue
                    </TabPanel>
                    <TabPanel value={index} index={2} data={orders.Cooking}>
                      Cooking
                    </TabPanel>
                  </div>
                </div>
              </Box>
            </Container>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

let brakePoints = [350, 500, 750];

interface AppProps {
  data: any;
  loadInd: any;
  toPrint: any;
  setValues: React.Dispatch<React.SetStateAction<any>>;
  businessName: string;
  businessAddress: {
    area: string;
    city: string;
  };
}

export const App: React.FC<AppProps> = ({
  data,
  loadInd,
  toPrint,
  setValues,
  businessName,
  businessAddress,
}) => {
  return (
    <div>
      {data && data.length === 0 ? (
        <div className="masonry-container-empty">
          <p className="mce_text">No orders right now</p>
        </div>
      ) : (
        <div className="masonry-container mt-10">
          <Masonry brakePoints={brakePoints}>
            {data.map((thisOne: any, id: any) => {
              return (
                <Tile
                  key={id}
                  loadInd={loadInd}
                  toPrint={toPrint}
                  data={thisOne}
                  Orderindex={id + 1}
                  setValues={setValues}
                  businessName={businessName}
                  businessAddress={businessAddress}
                  cardIndex={id}
                />
              );
            })}
          </Masonry>
        </div>
      )}
    </div>
  );
};

interface TileProps {
  data: any;
  loadInd: any;
  toPrint: any;
  Orderindex: number;
  setValues: React.Dispatch<React.SetStateAction<any>>;
  businessName: string;
  businessAddress: {
    area: string;
    city: string;
  };
  cardIndex: number;
}

const Tile: React.FC<TileProps> = ({
  data,
  loadInd,
  toPrint,
  Orderindex,
  setValues,
  businessName,
  businessAddress,
  cardIndex,
}) => {
  const statusChange = async (
    thisOrder: Order,
    status: string,
    index: number
  ) => {
    setValues((x: any) => ({ ...x, loadInd: thisOrder.id }));
    if (loadInd === null) {
      if (status === 'ready_for_delivery') {
        setValues((x: any) => ({ ...x, toPrint: data }));
      }
      if (status === 'cooking') {
        setValues((x: any) => ({ ...x, toPrint: data }));
      }
      var send_data = {
        id: thisOrder.id,
        newStatus: status,
      };
      const res: any = await Service.post('/orders/status', send_data);
      if (status === 'ready_for_delivery') {
        setValues((prev: any) => ({
          ...prev,
          errorPosition: 'success',
          errorText: 'Cooking Completed.',
          loadInd: null,
        }));
        handlePrintED(thisOrder);
      } else if (status === 'cooking') {
        setValues((prev: any) => ({
          ...prev,
          loadInd: null,
        }));
        handlePrintStart(thisOrder.id);
      } else {
        setValues((x: any) => ({ ...x, loadInd: null }));
      }
      setValues((x: any) => ({ ...x, loadInd: null }));
      if (res.error) {
        toastAlert('error', res.message);
      }
    }
  };

  // const statusChangeEachItem = async (data : Order, each : OrderItem) => {
  //   setValues((x : any) => ({ ...x, loadInd: data.id }));
  //   if (data.status === "preparing") return;
  //   var send_data = {};
  //   if (each.itemId)
  //     send_data = {
  //       id: data.id,
  //       itemId: each.itemId,
  //       identifierId: each.identifierId || null,
  //     };
  //   else if (each.comboId)
  //     send_data = {
  //       id: data.id,
  //       comboId: each.comboId,
  //     };
  //   else if (each.customId)
  //     send_data = {
  //       id: data.id,
  //       customId: each.customId,
  //     };
  //   else send_data = {};
  //   var isCookedTillNow = 1;
  //   data.ordersItems.forEach((element : any) => {
  //     if (element.isCooked && element.itemId !== each.itemId) isCookedTillNow++;
  //   });
  //   var print = isCookedTillNow === data.ordersItems	.length;
  //   if (print) setValues((x : any) => ({ ...x, toPrint: data }));
  //   const res : any = await Service.post("/orders/status", send_data)
  //   setValues((x : any) => ({ ...x, loadInd: null }));
  //     if(res.error) {
  //       toastAlert('error', res.message);
  //     }
  //     else if (res.success) {
  //       if (print) handlePrintED(data);
  //       setValues((prev : any) => ({
  //         ...prev,
  //         orders: res.orders,
  //       }));
  //     }
  // };

  // const FinishCookingPrint = (props : any) => {
  //   return (
  //     <>
  //       <div>
  //         <div
  //           id={`printId${props.i}`}
  //           className="printNeo"
  //           style={{ fontFamily: "Arial" }}
  //         >
  //           <div
  //             className="printNeoBody d-flex flex-column"
  //             style={{ fontFamily: "Arial", fontWeight: "400" }}
  //           >
  //             <div
  //               style={{
  //                 display: "flex",
  //                 flexDirection: "column",
  //                 alignItems: "center",
  //               }}
  //             >
  //               <span
  //                 style={{
  //                   fontSize: "16px",
  //                   lineHeight: "18px",
  //                   marginBottom: "5px",
  //                 }}
  //               >
  //                 {businessName}
  //               </span>
  //               <span
  //                 style={{
  //                   fontSize: "12px",
  //                   lineHeight: "14px",
  //                   marginBottom: "5px",
  //                 }}
  //               >
  //                 {businessAddress?.area} {businessAddress?.city}
  //               </span>
  //               <span
  //                 style={{
  //                   fontSize: "12px",
  //                   lineHeight: "14px",
  //                   marginBottom: "5px",
  //                 }}
  //               >
  //                 PAN NO.
  //               </span>
  //               <span
  //                 style={{
  //                   fontSize: "12px",
  //                   lineHeight: "14px",
  //                   marginBottom: "5px",
  //                 }}
  //               >
  //                 ABBREVIATED TAX INVOICE
  //               </span>
  //             </div>
  //             <span
  //               style={{
  //                 display: "flex",
  //                 justifyContent: "space-between",
  //                 marginTop: "10px",
  //                 fontSize: "12px",
  //                 lineHeight: "14px",
  //                 marginBottom: "5px",
  //               }}
  //             >
  //               <span>Order No. : #{props.data.referenceNumber}</span>
  //               <span> {formatDate(props.data.createdAt)} {formatTime(props.data.createdAt)}</span>
  //             </span>
  //             <div
  //               style={{
  //                 display: "flex",
  //                 flexDirection: "column",
  //                 gap: "5px",
  //               }}
  //             >
  //               {props.data?.delivery?.location?.phone ? (
  //                 <>
  //                   <span
  //                     style={{
  //                       fontSize: "12px",
  //                       lineHeight: "14px",
  //                     }}
  //                   >
  //                     Phone number : {props.data?.delivery?.location?.phone}
  //                   </span>
  //                 </>
  //               ) : (
  //                 ""
  //               )}
  //              {props?.data?.possiblePaymentMethod	&& <span
  //                 style={{
  //                   fontSize: "12px",
  //                   lineHeight: "14px",
  //                 }}
  //               >
  //                 Payment Method : {props.data.possiblePaymentMethod}
  //               </span>}
  //             </div>
  //             <div
  //               style={{
  //                 margin: "10px 0",
  //                 borderTop: "2px solid #000",
  //               }}
  //             />
  //             <div>
  //               {
  //                 props.data?.ordersItems?.map((each :any, i : number) => {
  //                   return (
  //                     <div
  //                       key={i}
  //                       style={{
  //                         display: "flex",
  //                         flexDirection: "column",
  //                         fontSize: "12px",
  //                         lineHeight: "14px",
  //                         marginBottom: "15px",
  //                       }}
  //                     >
  //                       <div>
  //                         <span>{each.quantity}</span>
  //                         <span style={{ marginLeft: "5px" }}>
  //                           {each.itemName}{" "}
  //                         </span>
  //                         {each?.modifiersInfo &&
  //                         each.modifiersInfo.length > 0 ? (
  //                           <span
  //                             style={{
  //                               width: "fit-content",
  //                               marginLeft: "auto",
  //                               float: "right",
  //                             }}
  //                           >
  //                             Rs.{each?.unitPrice}
  //                           </span>
  //                         ) : (
  //                           ""
  //                         )}

  //                         {!each?.modifiersInfo ||
  //                         each?.modifiersInfo.length === 0 ? (
  //                           <span
  //                             style={{
  //                               width: "fit-content",
  //                               marginLeft: "auto",
  //                               float: "right",
  //                             }}
  //                           >
  //                             Rs.{each.price}
  //                           </span>
  //                         ) : (
  //                           ""
  //                         )}
  //                       </div>
  //                       {each.modifiers && each.modifiers.length > 0 ? (
  //                         <>
  //                           {each.modifiers.map((mod : any, i : any) => (
  //                             <div
  //                               key={i}
  //                               style={{
  //                                 marginLeft: "10px",
  //                                 marginTop: "5px",
  //                               }}
  //                             >
  //                               <span style={{
  //                                 fontWeight:'500'
  //                               }}>{mod.name}</span>
  //                               <div>
  //                                 {
  //                                   mod?.options?.map((eachOp: any, indOp: number)=>(
  //                                     <span key={mod.name+indOp+eachOp.name}>{eachOp.name}</span>
  //                                   ))
  //                                 }
  //                               </div>
  //                             </div>
  //                           ))}
  //                         </>
  //                       ) : (
  //                         ""
  //                       )}
  //                       {each?.spiceLevel && (
  //                         <div
  //                           style={{
  //                             display: "flex",
  //                             justifyContent: "space-between",
  //                             marginLeft: "10px",
  //                             marginTop: "5px",
  //                           }}
  //                         >
  //                           <span>Spice level</span>
  //                           <span>{each.spiceLevel}</span>
  //                         </div>
  //                       )}

  //                       {each?.modifiersInfo &&
  //                       each?.modifiersInfo.length > 0 ? (
  //                         <div
  //                           style={{
  //                             display: "flex",
  //                             justifyContent: "flex-end",
  //                             marginTop: "5px",
  //                           }}
  //                         >
  //                           <span>Rs. {each.price}</span>
  //                         </div>
  //                       ) : (
  //                         ""
  //                       )}
  //                     </div>
  //                   );
  //                 })}
  //             </div>
  //             <div
  //               style={{
  //                 margin: "10px 0",
  //                 borderTop: "2px solid #000",
  //               }}
  //             />
  //             {props.data.subTotal && (
  //                 <div
  //                   style={{
  //                     display: "flex",
  //                     justifyContent: "space-between",
  //                     fontSize: "12px",
  //                     lineHeight: "14px",
  //                     marginBottom: "3px",
  //                   }}
  //                 >
  //                   <span>Subtotal</span>
  //                   <span>Rs. {props.data.subTotal}</span>
  //                 </div>
  //               )}
  //             {
  //             props.data?.deliveryCharge > 0 ? (
  //               <div
  //                 style={{
  //                   display: "flex",
  //                   justifyContent: "space-between",
  //                   fontSize: "12px",
  //                   lineHeight: "14px",
  //                   marginBottom: "3px",
  //                 }}
  //               >
  //                 <span>Delivery Charge</span>
  //                 <span>Rs. {props.data.deliveryCharge}</span>
  //               </div>
  //             ) : (
  //               ""
  //             )}
  //             {/* {props.data &&
  //             props.data.payment &&
  //             props.data.payment.discount ? (
  //               <>
  //                 {props.data.payment.discount > 0 && (
  //                   <div
  //                     style={{
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                       fontSize: "12px",
  //                       lineHeight: "14px",
  //                       marginBottom: "3px",
  //                     }}
  //                   >
  //                     <span>Discount</span>
  //                     <span>Rs. {props.data.payment.discount}</span>
  //                   </div>
  //                 )}
  //               </>
  //             ) : null} */}
  //             {props.data &&
  //             props.data?.payment &&
  //             props.data.payment?.promoDiscount ? (
  //               <>
  //                 {props.data.payment?.promoDiscount > 0 ? (
  //                   <div
  //                     style={{
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                       fontSize: "12px",
  //                       lineHeight: "14px",
  //                       marginBottom: "3px",
  //                     }}
  //                   >
  //                     <span>Promo Discount</span>
  //                     <span>- Rs. {props.data.payment?.promoDiscount}</span>
  //                   </div>
  //                 ) : (
  //                   ""
  //                 )}
  //               </>
  //             ) : (
  //               ""
  //             )}
  //             {props.data?.taxes?.map((each : any, i : any) => (
  //                   <div
  //                     style={{
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                       fontSize: "12px",
  //                       lineHeight: "14px",
  //                       marginBottom: "3px",
  //                     }}
  //                     key={i}
  //                   >
  //                     <span>{each.name} ({each.symbol==='%'? `${each.value}%` : ''})</span>
  //                     <span>
  //                       Rs. {each.amount}{" "}
  //                     </span>
  //                   </div>
  //                 ))
  //               }
  //             {props.data?.tip > 0 && (
  //               <div
  //                 style={{
  //                   display: "flex",
  //                   justifyContent: "space-between",
  //                   fontSize: "12px",
  //                   lineHeight: "14px",
  //                   marginBottom: "3px",
  //                 }}
  //               >
  //                 <span>Rider Tip</span>
  //                 <span>Rs. {props.data.tip}</span>
  //               </div>
  //             )}
  //             {props.data?.total && (
  //               <div
  //                 style={{
  //                   display: "flex",
  //                   justifyContent: "space-between",
  //                   fontWeight: "700",
  //                   fontSize: "14px",
  //                   lineHeight: "18px",
  //                   marginBottom: "3px",
  //                 }}
  //               >
  //                 <span>Total</span>
  //                 <span>Rs. {props.data.total}</span>
  //               </div>
  //             )}
  //             <div
  //               style={{
  //                 margin: "10px 0",
  //                 borderTop: "1px solid #000",
  //               }}
  //             />
  //             <div style={{ display: "flex", justifyContent: "center" }}>
  //               <p style={{ fontSize: "10px", lineHeight: "12px" }}>
  //                 THANK YOU FOR YOUR ORDER
  //               </p>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </>
  //   );
  // };

  const handlePrintED = (props: any) => {
    var prtContent = document.getElementById(`printId${props.referenceNumber}`);
    if (!prtContent) return;
    var printWindow: any | null = window.open(
      '',
      '',
      'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0'
    );
    printWindow.document.write(prtContent.innerHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePrintStart = (id: any) => {
    const prtContent = document.getElementById(`startprintKOT${id}`);
    if (!prtContent) return;
    const printWindow = window.open(
      '',
      '',
      'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0'
    );
    if (printWindow) {
      printWindow.document.write(prtContent.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } else {
      console.error('Failed to open a new window for printing.');
    }
  };
  return (
    <div className="tile text-black">
      <span
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '10px',
          fontSize: '14px',
          marginBottom: '5px',
          fontWeight: 600,
        }}
      >
        <span>Ordered at: {formatDateTime(new Date(data?.createdAt))}</span>
      </span>
      <span
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '10px',
          fontSize: '14px',
          marginBottom: '5px',
          fontWeight: 600,
        }}
      >
        <span> Table: {data?.tableName || data?.table?.name}</span>
      </span>
      {data.ordersItems?.map((each: any, i: any) => {
        return (
          <div key={i}>
            {i === 0 && (
              <>
                {/* <div className="d-flex justify-content-between  ">
                      <p className="tile_number">#{data.referenceNumber}</p>
                      <p>{formatTime(data.createdAt)}</p>
                    </div>
                    <Divider /> */}

                {/* <div className="flex justify-between mt-3 mb-2">
                      <div className="flex gap-1 items-center">
                        <ForkKnife size={26} color="#8943EF" />
                        <span>Cutlery</span>
                      </div>
                      {data.needCutlery ? "Yes" : "No"}
                    </div>
  
                    {data.note ? (
                      <div
                        className="tile_note mb-2"
                        style={{ wordWrap: "break-word" }}
                      >
                        {data.note}
                      </div>
                    ) : null} */}
              </>
            )}

            <Button
              className="tile_each_btn"
              // onClick={() => statusChangeEachItem(data, each)}
            >
              {/* item name */}
              <div
                className={
                  each.isCooked
                    ? 'tile_each_btn_title line_through_text'
                    : 'tile_each_btn_title'
                }
              >
                <span>
                  {each.itemName} {each.hasVariants && `(${each.variantName})`}
                </span>
                <span>x{each.quantity}</span>
              </div>

              {/* modifiers */}
              <div
                className={
                  each.modifiers && each.modifiers.length > 0
                    ? 'p-2 d-flex flex-column align-items-start w-100'
                    : 'align-items-start w-100'
                }
              >
                {each.modifiers &&
                  each.modifiers.length > 0 &&
                  each.modifiers.map((mod: any, ind: number) => {
                    return (
                      <div
                        key={ind}
                        className={
                          each.isCooked
                            ? 'tile_mod_text w-fit line_through_text font-semibold'
                            : 'tile_mod_text w-fit font-semibold'
                        }
                      >
                        {mod.name}
                        <div className="font-light w-fit">
                          {mod?.options?.map((eachOp: any, indexOp: number) => (
                            <span key={mod.name + eachOp.name + ind + indexOp}>
                              {eachOp.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                {each.spiceLevel && (
                  <div
                    className={
                      each.isCooked
                        ? 'tile_mod_spice_div line_through_text'
                        : 'tile_mod_spice_div'
                    }
                  >
                    <span className="tile_mod_text">Spice Level</span>
                    <span className="tile_mod_text ml-1">
                      - {each.spiceLevel}
                    </span>
                  </div>
                )}
              </div>

              {/* note */}
              {each.note?.length > 0 && (
                <div className="ml-4 text-secondary-0 mb-2">
                  <span>Note: </span>
                  <span className="italic">{each.note}</span>
                </div>
              )}
            </Button>

            {i === data.ordersItems.length - 1 && (
              <>
                {data.status === 'preparing' && (
                  <Button
                    className="start_cook_btn mt-2"
                    onClick={() => statusChange(data, 'cooking', i)}
                  >
                    <>
                      {loadInd === data.id ? (
                        <CircularProgress sx={{ color: '#fff' }} />
                      ) : (
                        'Start Cooking'
                      )}
                    </>
                  </Button>
                )}
                {data.status === 'cooking' && (
                  <Button
                    className="finish_cook_btn mt-2"
                    onClick={() => statusChange(data, 'ready_for_delivery', i)}
                  >
                    <>
                      {loadInd === data.id ? (
                        <CircularProgress sx={{ color: '#fff' }} />
                      ) : (
                        'Finish Cooking'
                      )}
                    </>
                  </Button>
                )}
                {/* {
                                          data.status === 'Cooking' &&
                                          < ComponentToPrint ref={componentRef} data={data} toPrint={toPrint} />} */}
                {/* {data.status === "cooking" && (
                      <FinishCookingPrint
                        i={data.referenceNumber}
                        data={data}
                        toPrint={toPrint}
                      />
                    )} */}
              </>
            )}
          </div>
        );
      })}
      {kotPrint({
        ...data,
      })}
    </div>
  );
};

interface MasonryProps {
  // Define your props here if any
  brakePoints: number[];
}

interface MasonryState {
  columns: number;
}

interface MasonryProps {
  brakePoints: number[];
  // Define other props here if any
  children: React.ReactNode | null | undefined; //
}

interface MasonryState {
  columns: number;
}

class Masonry extends React.Component<MasonryProps, MasonryState> {
  gridRef: React.RefObject<HTMLDivElement>;

  constructor(props: MasonryProps) {
    super(props);
    this.state = { columns: 1 };
    this.onResize = this.onResize.bind(this);
    this.gridRef = React.createRef<HTMLDivElement>();
  }
  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  getColumns(w: number): number {
    return (
      this.props.brakePoints.reduceRight((p, c, i) => {
        return c < w ? p : i;
      }, this.props.brakePoints.length) + 1
    );
  }

  onResize() {
    if (this.gridRef.current) {
      const width = this.gridRef.current.offsetWidth;
      const columns = this.getColumns(width);

      if (columns !== this.state.columns) {
        this.setState({ columns });
      }
    }
  }

  mapChildren() {
    const numC = this.state.columns ?? 1; // Default value if columns is undefined or null
    const childrenArray = React.Children.toArray(this.props.children); // Convert to array

    // Initialize an array of empty arrays
    const col: React.ReactNode[][] = Array.from({ length: numC }, () => []);

    // Distribute children into columns
    return childrenArray.reduce((p: any, c: any, i: any) => {
      p[i % numC].push(c);
      return p;
    }, col);
  }

  render() {
    return (
      <div
        className="masonry"
        ref={this.gridRef}
        // ref="Masonry"
      >
        {this.mapChildren().map((col: any, ci: number) => {
          return (
            <div className="column" key={ci}>
              {col.map((child: any, i: number) => {
                return <div key={i}>{child}</div>;
              })}
            </div>
          );
        })}
      </div>
    );
  }
}

// Mar 28 2022 5:32 AM
export const formatDateTimeKDS = (dateString: Date) => {
  var date = new Date(dateString);
  var hours = date.getHours();
  var minutes: any = date.getMinutes();
  var ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;
  var strTime = hours + ':' + minutes + ' ' + ampm;
  var strDate = formatDateNext(date);
  let split: any = strDate.split(' ');
  split = split[0] + ' ' + split[1];
  var day = getDay(date);
  return day + ' , ' + split + ' || ' + strTime;
};

export const formatDateNext = (dateString: Date) => {
  var original = new Date(dateString).toDateString();
  return original.slice(original.indexOf(' ') + 1);
};

const getDay = (date: Date) => {
  var day = new Date(date).getDay();
  switch (day) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    default:
      break;
  }
};

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};

//   5:32 AM
export const formatTime = (dateString: string) => {
  var date = new Date(dateString);
  var hours = date.getHours();
  var minutes: any = date.getMinutes();
  var ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;
  var strTime = hours + ':' + minutes + ' ' + ampm;
  return strTime;
};

export const formatDateTime = (dateString: any) => {
  if (!dateString) return '';
  if (typeof dateString === 'string' && !dateString.includes('Z'))
    dateString = dateString + 'Z';
  let options: any = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };
  return new Intl.DateTimeFormat('es-ES', options).format(new Date(dateString));
};

export default React.memo(KDSHome);
